// Letterhead shared by the clinical report PDFs — radiology
// (pages/radiology-technician/utils/printRadiologyReport.js) and lab
// (pages/lab-technician/utils/printLabReport.js).
//
// This is deliberately the same header/footer the discharge summary stamps (see
// generatePdfBlobFromHtml in pages/ipd/components/modals/DischargeSummaryModal.js),
// so every document a hospital hands a patient lines up page-for-page:
//
//   header  logo + "Address: …"             |   "Patient ID: …" + "Page N of M"
//           ───────────────────────────────────────────────────────── grey rule
//   footer  (last page only)  Prepared By ______   Checked By ______
//
// It is drawn onto the jsPDF instance *after* html2pdf has laid the content out,
// because only then is the page count known. Coordinates are mm on A4 (210×297)
// and mirror the discharge summary's exactly.
import { hospitalLogoBase64 } from "../pages/ipd/components/modals/hospitalLogo";
// The product mark that sits bottom-right of the v5 footer, beside "Powered by
// Hlthtek". Distinct from the facility's own logo, which is centred at the top.
// Same asset poPdf.js/grnPdf.js already footer-stamp.
import NavLogoHlthTek from "../assets/images/logo/NavLogo_new.png";

// Branding Management uploads the facility logo under this name; it is the same
// entry the navbar renders (components/Navbar/Navbar.jsx).
const LOGO_BRANDING_KEY = "collateral_image";

/**
 * Facility logo, configurable per hospital. Prefers the logo uploaded through
 * Branding Management (BrandingContext → `collateral_image`, base64 PNG) and
 * falls back to the bundled one for facilities that never uploaded theirs.
 */
export function resolveLogo(brandingData) {
  const uploaded = Array.isArray(brandingData)
    ? brandingData.find((item) => item?.name === LOGO_BRANDING_KEY)?.content
    : null;
  return uploaded ? `data:image/png;base64,${uploaded}` : hospitalLogoBase64;
}

const joinParts = (parts) =>
  parts.map((p) => String(p ?? "").trim()).filter(Boolean).join(", ");

/**
 * Facility address straight off `get_logged_in_user_info/` — the same fields and
 * the same order the OPD billing invoice prints
 * (pages/billing-payment/ViewInvoice.js buildFacilityAddress).
 */
const addressFromUserInfo = (userInfo) =>
  joinParts([
    userInfo?.facility_address_line_1,
    userInfo?.facility_address_line_2,
    userInfo?.facility_address_line_3,
    userInfo?.city,
    userInfo?.state,
    userInfo?.pincode ? `PIN: ${userInfo.pincode}` : null,
  ]);

/**
 * Address stashed in sessionStorage at login. Only two screens ever write these
 * keys (application-landing/index.js and doctor/LandingPage.js), so a session
 * that reached a workspace without passing through one — a lab or radiology user
 * landing straight in theirs, or a refresh on a deep link — has nothing here.
 * That is why this is the fallback and not the source.
 */
const addressFromSession = () =>
  joinParts([
    sessionStorage.getItem("facilityAddressLine1"),
    sessionStorage.getItem("facilityAddressLine2"),
    sessionStorage.getItem("facilityAddressLine3"),
  ]);

/**
 * Address and contact for the letterhead.
 *
 * @param {object} userInfo  PermissionContext's `userInfo`
 *   (get_logged_in_user_info/). Pass it and the address comes from the API, the
 *   same way every billing document builds it. Omit it and this falls back to
 *   sessionStorage, then to a placeholder.
 */
export function resolveFacilityDetails(userInfo) {
  const address = addressFromUserInfo(userInfo) || addressFromSession();

  return {
    address: address || "Hospital Address",
    // No login flow writes a facility contact today, and get_logged_in_user_info/
    // has no confirmed field for it — the tolerant lookup is here so it starts
    // printing the day the backend adds one, under whichever of these names.
    contact:
      userInfo?.facility_contact ||
      userInfo?.facility_contact_no ||
      userInfo?.facility_phone_no ||
      sessionStorage.getItem("facility_contact") ||
      "N/A",
  };
}

/**
 * Draw the header on every page and the signature footer on the last one.
 *
 * @param {object} pdf   jsPDF instance from html2pdf's `.get("pdf")`
 * @param {object} parts { logoBase64, address, contact, patientId, preparedBy, checkedBy }
 */
export function stampLetterhead(pdf, parts) {
  const {
    logoBase64,
    address,
    // No contact number is available yet, so the line is not printed — see the
    // commented-out contactText below. Both come back together.
    // contact,
    patientId,
    preparedBy,
    checkedBy,
  } = parts || {};

  const totalPages = pdf.internal.getNumberOfPages();
  const addressText = `Address: ${address || "Hospital Address"}`;
  // const contactText = `Contact: ${contact || "N/A"}`;
  const patientIdText = `Patient ID: ${patientId || "-"}`;

  // Read once — getImageProperties parses the whole image, and the logo is the
  // same on every page.
  let logo = null;
  if (logoBase64) {
    try {
      const imgProps = pdf.getImageProperties(logoBase64);
      const width = 45; // mm
      logo = {
        // Honour the actual encoding: a facility may upload a JPEG through
        // Branding Management even though the key is named *_image.
        format: imgProps.fileType || "PNG",
        width,
        height: (imgProps.height * width) / imgProps.width,
      };
    } catch (err) {
      // A corrupt or unsupported upload must not cost us the whole report.
      console.warn("Letterhead logo could not be read, printing without it:", err);
    }
  }

  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);

    // ── Header ───────────────────────────────────────────────────────────
    const topMargin = 8;
    let textStartY = topMargin + 10;

    if (logo) {
      pdf.addImage(logoBase64, logo.format, 15, topMargin, logo.width, logo.height);
      textStartY = topMargin + logo.height + 5;
    }

    pdf.setTextColor(128, 128, 128); // gray
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.text(addressText, 15, textStartY);
    // pdf.text(contactText, 15, textStartY + 5);

    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "bold");
    pdf.text(patientIdText, 195, 15, { align: "right" });

    pdf.setTextColor(128, 128, 128);
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "normal");
    pdf.text(`Page ${page} of ${totalPages}`, 195, 20, { align: "right" });

    pdf.setDrawColor(204, 204, 204);
    pdf.setLineWidth(0.5);
    pdf.line(15, 30, 195, 30);

    // ── Footer (signatures on the last page only) ────────────────────────
    if (page === totalPages) {
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.5);
      pdf.line(30, 275, 80, 275);
      pdf.line(130, 275, 180, 275);

      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);

      pdf.setFont("helvetica", "normal");
      pdf.text("Prepared By", 55, 280, { align: "center" });
      pdf.setFont("helvetica", "bold");
      pdf.text(preparedBy || "-", 55, 285, { align: "center" });

      pdf.setFont("helvetica", "normal");
      pdf.text("Checked By", 155, 280, { align: "center" });
      pdf.setFont("helvetica", "bold");
      pdf.text(checkedBy || "-", 155, 285, { align: "center" });
    }
  }
}

/**
 * The v5 letterhead.
 *
 * Measured from HlthTek_Purchase_Order_v5.pdf and recorded in
 * docs/superpowers/specs/2026-08-07-discharge-summary-fe-improvements-design.md §8.
 * Purely typographic — no filled bands, no rules — which is a deliberate
 * departure from the poPdf/ViewInvoice teal-block house style and the right
 * call for a clinical document: it stays legible on a mono ward printer and
 * doesn't flood toner.
 *
 * Deliberately additive: stampLetterhead() above is untouched because lab and
 * radiology print through it. Adopting v5 there later is a one-line swap.
 */
export const V5 = {
  COLORS: {
    primary: [31, 59, 109],
    muted: [91, 102, 117],
    body: [26, 31, 41],
    success: [22, 163, 74],
    danger: [192, 57, 43],
    rule: [209, 213, 219],
  },
  // Page 1 reserves the full centred letterhead; later pages a slim strip.
  // Repeating 78mm on every page of a three-page summary would cost a quarter
  // of each one.
  HEADER_H_MM: 72,
  // Reserve when the caller's own markup supplies the document title — the
  // discharge summary's templates already open with their own heading, so
  // drawing one here printed it twice. Clears the rule (49) and the status
  // pill (51.5–58.5).
  HEADER_H_CHROME_ONLY_MM: 62,
  CONTINUATION_H_MM: 30,
  FOOTER_TOP_MM: 268,
};

export function stampLetterheadV5(pdf, parts) {
  const {
    logoBase64,
    address,
    contact,
    facilityName,
    documentTitle,
    statusLabel,
    statusColor,
    subtitle,
    preparedBy,
    checkedBy,
    // Draw the document title in the page-1 header. Set false when the body
    // markup already carries its own heading — otherwise it prints twice.
    // documentTitle is still used for the "(continued)" strip on later pages.
    titleOnPage1 = true,
  } = parts || {};

  const W = 210;
  const H = 297;
  const SIDE = 14.8;
  const CX = W / 2;
  const totalPages = pdf.internal.getNumberOfPages();
  const { primary, muted, rule } = V5.COLORS;

  // Fit inside a box, preserving aspect. A width-only rule made a wide logo
  // small and would make a square one overrun the whole header.
  const measure = (src, maxW, maxH) => {
    if (!src) return null;
    try {
      const props = pdf.getImageProperties(src);
      const ratio = props.width / props.height;
      let width = maxW;
      let height = width / ratio;
      if (height > maxH) {
        height = maxH;
        width = height * ratio;
      }
      return { format: props.fileType || "PNG", width, height };
    } catch (err) {
      // A corrupt or unsupported upload must not cost us the whole document.
      console.warn("V5 letterhead image could not be read, printing without it:", err?.message);
      return null;
    }
  };

  // 58mm wide is the target, but the height cap is what actually protects the
  // layout: the facility name's baseline is at 36mm, so its glyph tops sit at
  // ~30.7mm and the logo must finish above that. Drawn from y=8, a 20mm cap
  // lands it at 28mm with 2.7mm clear.
  const facilityLogo = measure(logoBase64, 58, 20);
  const brandLogo = measure(NavLogoHlthTek, 22, 7);

  for (let page = 1; page <= totalPages; page += 1) {
    pdf.setPage(page);

    // ── Header ───────────────────────────────────────────────────────────
    if (page === 1) {
      if (facilityLogo) {
        pdf.addImage(
          logoBase64,
          facilityLogo.format,
          CX - facilityLogo.width / 2,
          8,
          facilityLogo.width,
          facilityLogo.height
        );
      }

      // The v5 mock's own coordinates put the facility name at 41.5mm, which
      // assumed a logo tall enough to fill the space above it. Ours is wider
      // than it is tall, so that left a 17mm hole under the logo. The block is
      // pulled up to sit just below whatever the logo actually occupies.
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(15);
      pdf.setTextColor(...primary);
      pdf.text(facilityName || "", CX, 36, { align: "center" });

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.2);
      pdf.setTextColor(...muted);
      if (address) pdf.text(address, CX, 41, { align: "center" });
      if (contact) pdf.text(contact, CX, 45, { align: "center" });

      // The rule that separates the facility identity from the document. Drawn
      // with a path op, which is why it never showed up in a text extraction.
      pdf.setDrawColor(...rule);
      pdf.setLineWidth(0.4);
      pdf.line(SIDE, 49, W - SIDE, 49);

      if (titleOnPage1) {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(17);
        pdf.setTextColor(...primary);
        pdf.text(documentTitle || "", SIDE, 57.5);

        if (subtitle) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(7.8);
          pdf.setTextColor(...muted);
          pdf.text(subtitle, SIDE, 61.5);
        }
      }

      // Status pill: an OUTLINED rounded rect, not bare text.
      if (statusLabel) {
        const colour = statusColor || muted;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(9);
        const textW = pdf.getTextWidth(statusLabel);
        const boxW = textW + 10;
        const boxH = 7;
        // Aligned with the title when there is one; otherwise it sits just
        // under the rule.
        const boxY = titleOnPage1 ? 52.5 : 51.5;
        pdf.setDrawColor(...colour);
        pdf.setLineWidth(0.5);
        pdf.roundedRect(W - SIDE - boxW, boxY, boxW, boxH, 1.6, 1.6, "S");
        pdf.setTextColor(...colour);
        pdf.text(statusLabel, W - SIDE - boxW / 2, boxY + boxH / 2 + 1.2, { align: "center" });
      }
    } else {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.setTextColor(...primary);
      pdf.text(facilityName || "", SIDE, 11);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.8);
      pdf.setTextColor(...muted);
      pdf.text(`${documentTitle || ""} (continued)`, SIDE, 17);

      pdf.setDrawColor(...rule);
      pdf.setLineWidth(0.4);
      pdf.line(SIDE, 21, W - SIDE, 21);
    }

    // ── Footer, every page ───────────────────────────────────────────────
    pdf.setDrawColor(...rule);
    pdf.setLineWidth(0.4);
    pdf.line(SIDE, 268, W - SIDE, 268);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...muted);
    // No dangling separator when there is no name to print.
    pdf.text(preparedBy ? `Prepared by \u00b7 ${preparedBy}` : "Prepared by", SIDE, 273.5);
    pdf.text(
      checkedBy ? `Checked by \u00b7 ${checkedBy}` : "Authorised Signatory",
      W - SIDE,
      273.5,
      { align: "right" }
    );

    // A second rule 9.5mm below the first, with a logo between them, read as
    // clutter in a 19mm band. One rule is enough to close the page.

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(7.8);
    pdf.setTextColor(...primary);
    pdf.text(facilityName || "", SIDE, 281);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7.4);
    pdf.setTextColor(...muted);
    pdf.text(
      "Powered by Hlthtek Optimus Private Limited \u00b7 www.hlthtek.com",
      SIDE,
      285
    );
    // Right-aligned, stopping clear of the product mark rather than floating
    // between it and the powered-by line.
    pdf.text(`Page ${page} of ${totalPages}`, W - SIDE - 28, 285, { align: "right" });

    if (brandLogo) {
      pdf.addImage(
        NavLogoHlthTek,
        brandLogo.format,
        W - SIDE - brandLogo.width,
        H - 18.5,
        brandLogo.width,
        brandLogo.height
      );
    }
  }
}
